import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend, ComposedChart, Area, AreaChart,
} from 'recharts';
import {
  Activity, TrendingUp, Award, XCircle, Users, Clock, Target,
  RefreshCw, Filter, ChevronRight,
} from 'lucide-react';
import { DISPOSITION_ORDER, dispositionMeta } from '@/lib/dispositions';
import { useNavigate } from 'react-router-dom';
import { REPORTS } from '@/constants/testIds';
import { LapsedTab, CohortTab, YoYTab, FunnelDetailed, SourceEffectiveness, GeoHeatTab } from '@/components/reports/AdvancedInsights';
import { ReportsRail, REPORT_SECTIONS } from '@/components/reports/ReportsRail';
import { MultiSelectChips } from '@/components/shared/MultiSelectChips';

const toOpts = (arr) => (arr || []).map((x) => ({ value: x, label: x }));

const CHART_HEIGHT = 320;
const PALETTE = ['#0F766E','#B45309','#4338CA','#0284C7','#7C3AED','#BE185D','#DC2626','#065F46','#1E40AF','#A16207'];

const DEFAULT_FILTERS = {
  from: '', to: '', clinic_name: '', source: '', subsource: '', disposition: '',
  consultant_name: '', assignee_email: '', item_name: '', cluster: '', city: '', q: '',
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeReport, setActiveReport] = useState(() => localStorage.getItem('hc.reports.active') || 'executive');
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hc.reports.collapsed') || '[]'); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem('hc.reports.active', activeReport); }, [activeReport]);
  useEffect(() => { localStorage.setItem('hc.reports.collapsed', JSON.stringify(collapsedSections)); }, [collapsedSections]);
  const toggleSection = (id) => setCollapsedSections(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const activeMeta = useMemo(() => {
    for (const sec of REPORT_SECTIONS) {
      const r = sec.reports.find(r => r.key === activeReport);
      if (r) return { section: sec, report: r };
    }
    return null;
  }, [activeReport]);

  const [filterOptions, setFilterOptions] = useState({ sources: [], clinics: [], subsources: [], cities: [], audiologists: [], dispositions: [] });
  const [audiologistOptions, setAudiologistOptions] = useState([]);
  const [clusterOptions, setClusterOptions] = useState([]);
  const [lapsedCount, setLapsedCount] = useState(null);
  const [inventoryKpi, setInventoryKpi] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [byClinic, setByClinic] = useState([]);
  const [bySource, setBySource] = useState([]);
  const [bySubsource, setBySubsource] = useState([]);
  const [byConsultant, setByConsultant] = useState([]);
  const [timeseries, setTimeseries] = useState([]);
  const [aging, setAging] = useState([]);
  const [executive, setExecutive] = useState(null);
  const [byCluster, setByCluster] = useState([]);
  const [byAudiologist, setByAudiologist] = useState([]);
  const [byItem, setByItem] = useState([]);
  const [patientsSummary, setPatientsSummary] = useState(null);
  const [patientsCohort, setPatientsCohort] = useState([]);
  const [patientsBySource, setPatientsBySource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  const paramsObj = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [k, f, c, s, sub, cons, ts, ag, ex, cl, au, it, ps, pc, pbs] = await Promise.all([
        api.get('/reports/kpi', { params: paramsObj }),
        api.get('/reports/disposition-funnel', { params: paramsObj }),
        api.get('/reports/by-clinic', { params: paramsObj }),
        api.get('/reports/by-source', { params: paramsObj }),
        api.get('/reports/by-subsource', { params: paramsObj }),
        api.get('/reports/by-consultant', { params: paramsObj }),
        api.get('/reports/timeseries', { params: { ...paramsObj, period } }),
        api.get('/reports/aging', { params: paramsObj }),
        api.get('/reports/executive', { params: paramsObj }),
        api.get('/reports/by-cluster', { params: paramsObj }),
        api.get('/reports/by-audiologist', { params: { ...paramsObj, limit: 40 } }),
        api.get('/reports/by-item', { params: { ...paramsObj, limit: 20 } }),
        api.get('/reports/patients-summary', { params: paramsObj }),
        api.get('/reports/patients-cohort', { params: paramsObj }),
        api.get('/reports/patients-by-source', { params: paramsObj }),
      ]);
      setKpi(k.data);
      setFunnel(f.data.data);
      setByClinic(c.data.data);
      setBySource(s.data.data);
      setBySubsource(sub.data.data);
      setByConsultant(cons.data.data);
      setTimeseries(ts.data.data);
      setAging(ag.data.data);
      setExecutive(ex.data);
      setByCluster(cl.data.data);
      setByAudiologist(au.data.data);
      setByItem(it.data.data);
      setPatientsSummary(ps.data);
      setPatientsCohort(pc.data.data);
      setPatientsBySource(pbs.data.data);

      // Rail-level counts (independent, non-blocking)
      api.get('/reports/lapsed-patients', { params: { ...paramsObj, limit: 1 } })
        .then(r => setLapsedCount(r.data.total_lapsed)).catch(() => {});
      api.get('/inventory/kpi').then(r => setInventoryKpi(r.data)).catch(() => {});
    } catch (err) {
      toast.error('Failed to load reports');
    } finally { setLoading(false); }
  }, [paramsObj, period]);

  useEffect(() => {
    api.get('/reports/filters').then(r => setFilterOptions(r.data)).catch(() => {});
    // populate audiologist + cluster dropdowns once
    api.get('/reports/by-audiologist', { params: { limit: 200 } })
      .then(r => setAudiologistOptions(r.data.data.map(x => ({ email: x.audiologist, name: x.name, sold: x.sold }))))
      .catch(() => {});
    api.get('/clusters').then(r => setClusterOptions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const railCounts = useMemo(() => ({
    lapsed: lapsedCount,
    executive: kpi?.total_tickets,
    audiologists: byAudiologist?.length || undefined,
    clinics: byClinic?.length || undefined,
    clusters: byCluster?.length || undefined,
    consultants: byConsultant?.length || undefined,
    items: byItem?.length || undefined,
    sources: bySource?.length || undefined,
    geo: byClinic?.length || undefined,
  }), [lapsedCount, kpi, byAudiologist, byClinic, byCluster, byConsultant, byItem, bySource]);

  const funnelData = useMemo(() => {
    const map = Object.fromEntries(funnel.map(f => [f.key, f.count]));
    return DISPOSITION_ORDER.map(k => ({
      key: k,
      label: dispositionMeta(k).label,
      count: map[k] || 0,
      color: dispositionMeta(k).text,
    })).filter(x => x.count > 0);
  }, [funnel]);

  // Drilldown to tickets
  const drillToTickets = (partial) => {
    const p = new URLSearchParams();
    Object.entries({ ...paramsObj, ...partial }).forEach(([k, v]) => v && p.set(k, v));
    navigate(`/tickets?${p.toString()}`);
  };

  return (
    <div className="flex-1 flex overflow-hidden bg-stone-50" data-testid={REPORTS.page}>
      <ReportsRail
        active={activeReport}
        onSelect={setActiveReport}
        collapsedSections={collapsedSections}
        onToggleSection={toggleSection}
        counts={railCounts}
        kpi={kpi}
      />
      <div className="flex-1 overflow-auto hc-scrollbar">
      <div className="max-w-[1600px] mx-auto p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ color: activeMeta?.section.color || '#2D3982' }}>
              {activeMeta?.section.icon && <activeMeta.section.icon className="w-3 h-3" />}
              {activeMeta?.section.label || 'Reports'} <span className="text-stone-400">·</span> {activeMeta?.report.label || ''}
            </div>
            <h1 className="font-heading text-2xl font-bold hc-section-title">{activeMeta?.report.label || 'Reports'}</h1>
            <p className="text-sm text-stone-500 mt-0.5">
              {activeMeta?.report.desc || (kpi ? `Analyzing ${kpi.total_tickets.toLocaleString()} patients` : 'Loading real-time data…')}
            </p>
          </div>
          <Button
            variant="outline" size="sm"
            className="h-8 border-stone-300"
            onClick={loadAll}
            data-testid="reports-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Sticky filter bar */}
        <Card className="p-3 border-stone-200 sticky top-0 z-20 bg-white" data-testid={REPORTS.filterBar}>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-stone-500" />
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Filters</span>
            <div className="flex items-center gap-1">
              <Input
                type="date" value={filters.from}
                onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))}
                className="h-8 w-36 text-xs" data-testid={REPORTS.filterFrom}
              />
              <span className="text-stone-400 text-xs">→</span>
              <Input
                type="date" value={filters.to}
                onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))}
                className="h-8 w-36 text-xs" data-testid={REPORTS.filterTo}
              />
            </div>
            <MultiSelectChips label="Cities" value={filters.city} options={toOpts(filterOptions.cities)}
              onChange={(v) => setFilters(f => ({ ...f, city: v }))} testId="reports-filter-city" width="w-32" />
            <MultiSelectChips label="Sources" value={filters.source} options={toOpts(filterOptions.sources)}
              onChange={(v) => setFilters(f => ({ ...f, source: v }))} testId={REPORTS.filterSource} width="w-36" />
            <MultiSelectChips label="Clinics" value={filters.clinic_name} options={toOpts(filterOptions.clinics)}
              onChange={(v) => setFilters(f => ({ ...f, clinic_name: v }))} testId={REPORTS.filterClinic} width="w-44" />
            <MultiSelectChips label="Statuses" value={filters.disposition}
              options={DISPOSITION_ORDER.map(d => ({ value: d, label: dispositionMeta(d).label }))}
              onChange={(v) => setFilters(f => ({ ...f, disposition: v }))} testId={REPORTS.filterDisposition} />
            <MultiSelectChips label="Sub-sources" value={filters.subsource} options={toOpts(filterOptions.subsources)}
              onChange={(v) => setFilters(f => ({ ...f, subsource: v }))} testId="reports-filter-subsource" />
            <Select value={filters.assignee_email || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, assignee_email: v === 'all' ? '' : v }))}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="reports-filter-audiologist">
                <SelectValue placeholder="Audiologist" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All Audiologists</SelectItem>
                {audiologistOptions.map(a => (<SelectItem key={a.email} value={a.email}>{a.name} · {a.sold} sold</SelectItem>))}
              </SelectContent>
            </Select>
            <MultiSelectChips label="Clusters" value={filters.cluster}
              options={clusterOptions.map(c => ({ value: c.name, label: c.name }))}
              onChange={(v) => setFilters(f => ({ ...f, cluster: v }))} testId="reports-filter-cluster" width="w-36" />
            <Input
              value={filters.item_name}
              onChange={(e) => setFilters(f => ({ ...f, item_name: e.target.value }))}
              placeholder="Item / Device…"
              className="h-8 w-40 text-xs"
              data-testid="reports-filter-item"
            />
            <Input
              value={filters.q}
              onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
              placeholder="Search patient/mobile…"
              className="h-8 w-52 text-xs"
              data-testid="reports-filter-search"
            />
            <Button
              size="sm" variant="ghost" className="h-8 text-xs"
              onClick={() => setFilters(DEFAULT_FILTERS)}
              data-testid={REPORTS.resetFilters}
            >
              Reset
            </Button>
            <div className="flex-1" />
            <Button size="sm" variant="outline" className="h-8 text-xs border-stone-300" onClick={() => drillToTickets({})}>
              View patients <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </Card>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <KPI label="Total" value={kpi?.total_tickets} icon={Users} tone="default" tid="total" onClick={() => drillToTickets({})} />
          <KPI label="Sold" value={kpi?.sold} icon={Award} tone="success" tid="sold" onClick={() => drillToTickets({ disposition: 'sold' })} />
          <KPI label="Conv. %" value={kpi ? `${kpi.conversion_rate}%` : null} icon={TrendingUp} tone="success" tid="conv" onClick={() => drillToTickets({ disposition: 'sold' })} />
          <KPI label="Booked" value={kpi?.booked} icon={Activity} tone="info" tid="booked" onClick={() => drillToTickets({ disposition_group: 'booked' })} />
          <KPI label="On Trial" value={kpi?.on_trial} icon={Target} tone="info" tid="trial" onClick={() => drillToTickets({ disposition: 'on_trial' })} />
          <KPI label="Active Pipeline" value={kpi?.active_pipeline} icon={Activity} tone="warning" tid="active" onClick={() => drillToTickets({ disposition_group: 'active' })} />
          <KPI label="Follow-ups" value={kpi?.followups_pending} icon={Clock} tone="warning" tid="followups" onClick={() => drillToTickets({ disposition_group: 'followups' })} />
          <KPI label="Lost" value={kpi?.lost} icon={XCircle} tone="danger" tid="lost" onClick={() => drillToTickets({ disposition_group: 'lost' })} />
        </div>

        {/* Tabs (rail-driven) */}
        <Tabs value={activeReport} onValueChange={setActiveReport} className="w-full">
          <TabsList className="hidden">
            <TabsTrigger value="executive" />
            <TabsTrigger value="lapsed" />
            <TabsTrigger value="funnel" />
            <TabsTrigger value="cohort" />
            <TabsTrigger value="yoy" />
            <TabsTrigger value="geo" />
            <TabsTrigger value="clusters" />
            <TabsTrigger value="clinics" />
            <TabsTrigger value="audiologists" />
            <TabsTrigger value="items" />
            <TabsTrigger value="customers" />
            <TabsTrigger value="sources" />
            <TabsTrigger value="consultants" />
            <TabsTrigger value="trends" />
            <TabsTrigger value="aging" />
            <TabsTrigger value="source-effectiveness" />
          </TabsList>

          {/* --- ADVANCED INSIGHTS TABS --- */}
          <TabsContent value="lapsed" className="mt-3"><LapsedTab params={paramsObj} /></TabsContent>
          <TabsContent value="cohort" className="mt-3"><CohortTab params={paramsObj} /></TabsContent>
          <TabsContent value="yoy" className="mt-3"><YoYTab params={paramsObj} /></TabsContent>
          <TabsContent value="geo" className="mt-3"><GeoHeatTab params={paramsObj} /></TabsContent>
          <TabsContent value="source-effectiveness" className="mt-3"><SourceEffectiveness params={paramsObj} /></TabsContent>


          {/* Executive */}
          <TabsContent value="executive" className="mt-3 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KPI label="Total Patients" value={executive?.total_tickets} icon={Users} tone="default" tid="exec-total" onClick={() => drillToTickets({})} />
              <KPI label="Sold" value={executive?.sold} icon={Award} tone="success" tid="exec-sold" onClick={() => drillToTickets({ disposition: 'sold' })} />
              <KPI label="Conversion" value={executive ? `${executive.conversion_rate}%` : null} icon={TrendingUp} tone="success" tid="exec-conv" onClick={() => drillToTickets({ disposition: 'sold' })} />
              <KPI label="Est. Revenue" value={executive ? `₹${(executive.revenue_estimate / 10000000).toFixed(2)}Cr` : null} icon={Award} tone="success" tid="exec-rev" onClick={() => drillToTickets({ disposition: 'sold' })} />
              <KPI label="Booked" value={executive?.booked} icon={Activity} tone="info" tid="exec-booked" onClick={() => drillToTickets({ disposition_group: 'booked' })} />
              <KPI label="Active Pipeline" value={executive?.active_pipeline} icon={Target} tone="warning" tid="exec-active" onClick={() => drillToTickets({ disposition_group: 'active' })} />
              <KPI label="Lost" value={executive?.lost} icon={XCircle} tone="danger" tid="exec-lost" onClick={() => drillToTickets({ disposition_group: 'lost' })} />
              <KPI label="Clusters" value={byCluster.filter(c => c.cluster !== '(Unassigned)').length} icon={Users} tone="default" tid="exec-clusters" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Top Clinics — Sold" subtitle="Board snapshot — click to drill">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={executive?.top_clinics || []} layout="vertical" margin={{ left: 100, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="sold" fill="#059669" radius={[0, 4, 4, 0]} cursor="pointer"
                      onClick={(d) => drillToTickets({ clinic_name: d.name, disposition: 'sold' })} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Top Sources — Sold" subtitle="Lead channels driving conversions">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={executive?.top_sources || []} layout="vertical" margin={{ left: 100, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="sold" fill="#0F766E" radius={[0, 4, 4, 0]} cursor="pointer"
                      onClick={(d) => drillToTickets({ source: d.name, disposition: 'sold' })} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </TabsContent>

          {/* Clusters */}
          <TabsContent value="clusters" className="mt-3 space-y-3">
            {byCluster.length === 0 || (byCluster.length === 1 && byCluster[0].cluster === '(Unassigned)') ? (
              <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-900">
                <strong>No clusters defined yet.</strong> Go to <a href="/admin/clusters" className="underline font-medium">Admin → Clusters</a> to group clinics into business clusters (regions), then this tab will show roll-up metrics per cluster.
              </Card>
            ) : null}
            <ChartCard title="Cluster Performance" subtitle="Total · Sold · Booked · Lost per cluster">
              <div data-testid={REPORTS.chart('by-cluster')}>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT + 40}>
                <BarChart data={byCluster}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="cluster" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sold" stackId="a" fill="#059669" name="Sold" cursor="pointer"
                    onClick={(d) => drillToTickets({ cluster: d.cluster !== '(Unassigned)' ? d.cluster : undefined, disposition: 'sold' })} />
                  <Bar dataKey="booked" stackId="a" fill="#0284C7" name="Booked" cursor="pointer"
                    onClick={(d) => drillToTickets({ cluster: d.cluster !== '(Unassigned)' ? d.cluster : undefined, disposition: 'booked' })} />
                  <Bar dataKey="lost" stackId="a" fill="#B91C1C" name="Lost" cursor="pointer"
                    onClick={(d) => drillToTickets({ cluster: d.cluster !== '(Unassigned)' ? d.cluster : undefined, disposition_group: 'lost' })} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            </ChartCard>
            <DataTable
              testid={REPORTS.table('cluster')}
              rows={byCluster}
              cols={[
                { key: 'cluster', label: 'Cluster', className: 'font-medium' },
                { key: 'clinics', label: 'Clinics', numeric: true },
                { key: 'total', label: 'Total', numeric: true, bar: true, barMax: Math.max(...byCluster.map(r => r.total || 0), 1) },
                { key: 'sold', label: 'Sold', numeric: true },
                { key: 'booked', label: 'Booked', numeric: true },
                { key: 'lost', label: 'Lost', numeric: true },
                { key: 'conversion', label: 'Conv %', numeric: true, format: (v) => `${v}%` },
              ]}
              onRowClick={(r) => drillToTickets({ cluster: r.cluster !== '(Unassigned)' ? r.cluster : undefined })}
            />
          </TabsContent>

          {/* Audiologists */}
          <TabsContent value="audiologists" className="mt-3 space-y-3">
            <ChartCard title="Top 20 Audiologists by Sales" subtitle="Ranked by patients sold across all clinics">
              <div data-testid={REPORTS.chart('by-audiologist')}>
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={byAudiologist.slice(0, 20)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-40} textAnchor="end" height={110} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sold" fill="#059669" name="Sold" cursor="pointer"
                    onClick={(d) => drillToTickets({ assignee_email: d.audiologist, disposition: 'sold' })} />
                  <Bar dataKey="booked" fill="#0284C7" name="Booked" cursor="pointer"
                    onClick={(d) => drillToTickets({ assignee_email: d.audiologist, disposition: 'booked' })} />
                  <Bar dataKey="trials" fill="#7C3AED" name="Trials" cursor="pointer"
                    onClick={(d) => drillToTickets({ assignee_email: d.audiologist, disposition: 'on_trial' })} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            </ChartCard>
            <DataTable
              testid={REPORTS.table('audiologist')}
              rows={byAudiologist}
              cols={[
                { key: 'name', label: 'Audiologist', className: 'font-medium' },
                { key: 'audiologist', label: 'Email', render: (r) => <span className="font-mono text-xs text-stone-500">{r.audiologist}</span> },
                { key: 'total', label: 'Total', numeric: true, bar: true, barMax: Math.max(...byAudiologist.map(r => r.total || 0), 1) },
                { key: 'sold', label: 'Sold', numeric: true },
                { key: 'booked', label: 'Booked', numeric: true },
                { key: 'trials', label: 'Trials', numeric: true },
                { key: 'conversion', label: 'Conv %', numeric: true, format: (v) => `${v}%` },
                { key: 'revenue', label: 'Revenue', numeric: true, format: (v) => v ? `₹${(v / 100000).toFixed(1)}L` : '—' },
              ]}
              onRowClick={(r) => drillToTickets({ assignee_email: r.audiologist })}
            />
          </TabsContent>

          {/* Items / Devices */}
          <TabsContent value="items" className="mt-3 space-y-3">
            <ChartCard title="Top 20 Devices Sold" subtitle="By units sold — revenue in lakhs">
              <div data-testid={REPORTS.chart('by-item')}>
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={byItem.slice(0, 20)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="item" tick={{ fontSize: 8 }} interval={0} angle={-40} textAnchor="end" height={140} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} unit="L" />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="l" dataKey="sold" fill="#059669" name="Units sold" cursor="pointer"
                    onClick={(d) => drillToTickets({ item_name: d.item, disposition: 'sold' })} />
                  <Line yAxisId="r" type="monotone" dataKey={(d) => (d.revenue / 100000)} stroke="#B45309" strokeWidth={2} name="Revenue (₹L)" dot={{ r: 3 }} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            </ChartCard>
            <DataTable
              testid={REPORTS.table('item')}
              rows={byItem}
              cols={[
                { key: 'item', label: 'Device / Item', className: 'font-medium' },
                { key: 'total', label: 'Total Patients', numeric: true, bar: true, barMax: Math.max(...byItem.map(r => r.total || 0), 1) },
                { key: 'sold', label: 'Sold', numeric: true },
                { key: 'conversion', label: 'Conv %', numeric: true, format: (v) => `${v}%` },
                { key: 'revenue', label: 'Revenue', numeric: true, format: (v) => v ? `₹${(v / 100000).toFixed(1)}L` : '—' },
              ]}
              onRowClick={(r) => drillToTickets({ item_name: r.item })}
            />
          </TabsContent>

          {/* Funnel */}
          <TabsContent value="funnel" className="mt-3 space-y-4">
            <FunnelDetailed params={paramsObj} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Disposition Distribution" subtitle="Patient count per status — click to drill down">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={funnelData} layout="vertical" margin={{ left: 100, right: 30 }} data-testid={REPORTS.chart('funnel')}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="label" type="category" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #E7E5E4' }} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}
                      onClick={(d) => drillToTickets({ disposition: d.key })}
                      cursor="pointer">
                      {funnelData.map((d, i) => (<Cell key={i} fill={d.color} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Outcome Split" subtitle="Sold vs Booked vs In-Progress vs Lost">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Sold', value: kpi?.sold || 0, color: '#059669' },
                        { name: 'Booked', value: kpi?.booked || 0, color: '#0284C7' },
                        { name: 'On Trial', value: kpi?.on_trial || 0, color: '#7C3AED' },
                        { name: 'Active', value: kpi?.active_pipeline || 0, color: '#B45309' },
                        { name: 'Follow-up', value: kpi?.followups_pending || 0, color: '#BE185D' },
                        { name: 'Lost', value: kpi?.lost || 0, color: '#B91C1C' },
                      ]}
                      cx="50%" cy="50%" outerRadius={100} innerRadius={55}
                      dataKey="value" nameKey="name" label={(e) => `${e.name}: ${e.value}`}
                      labelLine={false}
                    >
                      {['#059669','#0284C7','#7C3AED','#B45309','#BE185D','#B91C1C'].map((c, i) => (<Cell key={i} fill={c} />))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </TabsContent>

          {/* Clinics */}
          <TabsContent value="clinics" className="mt-3 space-y-3">
            <ChartCard title="Top 15 Clinics by Volume" subtitle="Sold · Booked · Lost — click clinic bar to filter">
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={byClinic.slice(0, 15)} data-testid={REPORTS.chart('by-clinic')}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="clinic" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" height={110} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sold" stackId="a" fill="#059669" name="Sold" cursor="pointer"
                    onClick={(d) => drillToTickets({ clinic_name: d.clinic, disposition: 'sold' })} />
                  <Bar dataKey="booked" stackId="a" fill="#0284C7" name="Booked" cursor="pointer"
                    onClick={(d) => drillToTickets({ clinic_name: d.clinic, disposition: 'booked' })} />
                  <Bar dataKey="trials" stackId="a" fill="#7C3AED" name="Trials" cursor="pointer"
                    onClick={(d) => drillToTickets({ clinic_name: d.clinic, disposition: 'on_trial' })} />
                  <Bar dataKey="lost" stackId="a" fill="#B91C1C" name="Lost" cursor="pointer"
                    onClick={(d) => drillToTickets({ clinic_name: d.clinic, disposition_group: 'lost' })} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <DataTable
              testid={REPORTS.table('clinic')}
              rows={byClinic}
              cols={[
                { key: 'clinic', label: 'Clinic', className: 'font-medium' },
                { key: 'total', label: 'Total', numeric: true },
                { key: 'sold', label: 'Sold', numeric: true },
                { key: 'booked', label: 'Booked', numeric: true },
                { key: 'trials', label: 'Trials', numeric: true },
                { key: 'lost', label: 'Lost', numeric: true },
                { key: 'conversion', label: 'Conv %', numeric: true, format: (v) => `${v}%`, bar: true },
              ]}
              onRowClick={(r) => drillToTickets({ clinic_name: r.clinic })}
            />
          </TabsContent>

          {/* Customers / Patients */}
          <TabsContent value="customers" className="mt-3 space-y-3">
            <Card className="p-2 border-stone-200 bg-stone-50 text-[11px] text-stone-600">
              <strong>Note:</strong> repeat-rate reflects unique mobile-number matches across the Fundle sync. If a patient walks in twice with different phone numbers Fundle registers them as two records — HearClear OS will start deduping by name+DOB once you begin creating new patients here.
            </Card>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KPI label="Total Patients" value={patientsSummary?.total_patients} icon={Users} tone="default" tid="pat-total" />
              <KPI label="Converted" value={patientsSummary?.converted_patients} icon={Award} tone="success" tid="pat-converted" onClick={() => drillToTickets({ disposition: 'sold' })} />
              <KPI label="Repeat Rate" value={patientsSummary ? `${patientsSummary.repeat_rate}%` : null} icon={TrendingUp} tone="info" tid="pat-repeat" />
              <KPI label="Patient Conv %" value={patientsSummary ? `${patientsSummary.conversion_rate_patients}%` : null} icon={Target} tone="success" tid="pat-conv" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Patient Frequency Distribution" subtitle="How many patients bring 1 / 2 / 3–5 / 6+ visits">
                <div data-testid={REPORTS.chart('patients-dist')}>
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={patientsSummary ? [
                    { bucket: '1 visit', count: patientsSummary.distribution['1_ticket'] || 0 },
                    { bucket: '2 visits', count: patientsSummary.distribution['2_tickets'] || 0 },
                    { bucket: '3–5', count: patientsSummary.distribution['3_5_tickets'] || 0 },
                    { bucket: '6+', count: patientsSummary.distribution['6plus_tickets'] || 0 },
                  ] : []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {['#78716C','#0284C7','#059669','#B45309'].map((c, i) => (<Cell key={i} fill={c} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </ChartCard>
              <ChartCard title="Unique Patients by Source" subtitle="Deduplicated patient counts per lead channel">
                <div data-testid={REPORTS.chart('patients-source')}>
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={patientsBySource.slice(0, 10)} layout="vertical" margin={{ left: 100, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="source" type="category" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="patients" fill="#0F766E" radius={[0, 4, 4, 0]} cursor="pointer"
                      onClick={(d) => drillToTickets({ source: d.source })} />
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>
            <ChartCard title="Acquisition Cohorts" subtitle="Unique patients acquired per month + their eventual conversion">
              <div data-testid={REPORTS.chart('patients-cohort')}>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <ComposedChart data={[...patientsCohort].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="cohort" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area yAxisId="l" type="monotone" dataKey="patients" fill="#E7E5E4" stroke="#78716C" name="Patients acquired" />
                  <Bar yAxisId="l" dataKey="converted" fill="#059669" name="Converted" />
                  <Line yAxisId="r" type="monotone" dataKey="conversion" stroke="#B45309" strokeWidth={2} name="Conv %" />
                </ComposedChart>
              </ResponsiveContainer>
              </div>
            </ChartCard>
            <DataTable
              testid={REPORTS.table('patients-source')}
              rows={patientsBySource}
              cols={[
                { key: 'source', label: 'Source', className: 'font-medium' },
                { key: 'patients', label: 'Unique Patients', numeric: true, bar: true, barMax: Math.max(...patientsBySource.map(r => r.patients || 0), 1) },
                { key: 'converted', label: 'Converted', numeric: true },
                { key: 'repeat', label: 'Repeat', numeric: true },
                { key: 'conversion', label: 'Conv %', numeric: true, format: (v) => `${v}%` },
                { key: 'repeat_rate', label: 'Repeat %', numeric: true, format: (v) => `${v}%` },
              ]}
              onRowClick={(r) => drillToTickets({ source: r.source })}
            />
          </TabsContent>

          {/* Sources */}
          <TabsContent value="sources" className="mt-3 space-y-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Volume by Source" subtitle="">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={bySource} data-testid={REPORTS.chart('by-source')}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis dataKey="source" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total" fill="#0F766E" name="Total" cursor="pointer"
                      onClick={(d) => drillToTickets({ source: d.source })} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Conversion % by Source" subtitle="Sold ÷ Total">
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart data={bySource}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis dataKey="source" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 10 }} unit="%" />
                    <Tooltip contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="conversion" fill="#059669" name="Conv%" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <SourceEffectiveness params={paramsObj} />
            <ChartCard title="Top Sub-sources" subtitle="Volume & conversion per specific lead channel">
              <DataTable
                testid={REPORTS.table('subsource')}
                rows={bySubsource}
                cols={[
                  { key: 'source', label: 'Source' },
                  { key: 'subsource', label: 'Sub-source', className: 'font-medium' },
                  { key: 'total', label: 'Volume', numeric: true, bar: true, barMax: Math.max(...bySubsource.map(r => r.total || 0), 1) },
                  { key: 'sold', label: 'Sold', numeric: true },
                  { key: 'conversion', label: 'Conv %', numeric: true, format: (v) => `${v}%` },
                ]}
                onRowClick={(r) => drillToTickets({ source: r.source })}
              />
            </ChartCard>
          </TabsContent>

          {/* Consultants */}
          <TabsContent value="consultants" className="mt-3 space-y-3">
            <ChartCard title="Top Consultants by Sales" subtitle="Ranked by patients sold">
              <ResponsiveContainer width="100%" height={420}>
                <BarChart
                  data={[...byConsultant].sort((a, b) => b.sold - a.sold).slice(0, 20)}
                  data-testid={REPORTS.chart('by-consultant')}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="consultant" tick={{ fontSize: 9 }} interval={0} angle={-40} textAnchor="end" height={120} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sold" fill="#059669" name="Sold" cursor="pointer"
                    onClick={(d) => drillToTickets({ consultant_name: d.consultant, disposition: 'sold' })} />
                  <Bar dataKey="booked" fill="#0284C7" name="Booked" cursor="pointer"
                    onClick={(d) => drillToTickets({ consultant_name: d.consultant })} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <DataTable
              testid={REPORTS.table('consultant')}
              rows={byConsultant}
              cols={[
                { key: 'consultant', label: 'Consultant', className: 'font-medium' },
                { key: 'total', label: 'Total', numeric: true, bar: true, barMax: Math.max(...byConsultant.map(r => r.total || 0), 1) },
                { key: 'sold', label: 'Sold', numeric: true },
                { key: 'booked', label: 'Booked', numeric: true },
                { key: 'conversion', label: 'Conv %', numeric: true, format: (v) => `${v}%` },
              ]}
              onRowClick={(r) => drillToTickets({ consultant_name: r.consultant })}
            />
          </TabsContent>

          {/* Trends */}
          <TabsContent value="trends" className="mt-3 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Period</span>
              {['day','week','month'].map(p => (
                <button key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-xs uppercase tracking-wider font-semibold rounded border ${
                    period === p ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300'
                  }`}
                >{p}</button>
              ))}
            </div>
            <ChartCard title="Patient Volume Over Time" subtitle="Total · Sold · Booked · Lost">
              <ResponsiveContainer width="100%" height={CHART_HEIGHT + 40}>
                <ComposedChart data={timeseries} data-testid={REPORTS.chart('trends')}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="total" fill="#E7E5E4" stroke="#78716C" name="Total" />
                  <Line type="monotone" dataKey="sold" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Sold" />
                  <Line type="monotone" dataKey="booked" stroke="#0284C7" strokeWidth={2} dot={{ r: 3 }} name="Booked" />
                  <Line type="monotone" dataKey="lost" stroke="#B91C1C" strokeWidth={2} dot={{ r: 3 }} name="Lost" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </TabsContent>

          {/* Aging */}
          <TabsContent value="aging" className="mt-3">
            <ChartCard title="Open Patient Aging" subtitle="Days since lead-gen for non-terminal patients">
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={aging} data-testid={REPORTS.chart('aging')}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {aging.map((_, i) => (<Cell key={i} fill={PALETTE[i % PALETTE.length]} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </TabsContent>
        </Tabs>
      </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, tone = 'default', tid, onClick }) {
  const tones = {
    default: 'bg-white border-stone-200 text-stone-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    danger: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-sky-50 border-sky-200 text-sky-800',
  }[tone];
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      data-testid={REPORTS.kpi(tid)}
      className={`p-3 border rounded-md text-left ${tones} ${onClick ? 'hover:shadow-sm cursor-pointer' : 'cursor-default'} transition-shadow`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-60" />
      </div>
      <div className="font-heading font-bold text-xl tabular-nums">
        {value == null ? '—' : (typeof value === 'number' ? value.toLocaleString() : value)}
      </div>
    </button>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <Card className="p-4 border-stone-200">
      <div className="mb-3">
        <div className="font-heading font-bold text-stone-900">{title}</div>
        {subtitle && <div className="text-xs text-stone-500">{subtitle}</div>}
      </div>
      {children}
    </Card>
  );
}

function DataTable({ rows, cols, onRowClick, testid }) {
  if (!rows || rows.length === 0) {
    return <div className="text-center text-sm text-stone-400 py-6">No data.</div>;
  }
  return (
    <Card className="overflow-hidden border-stone-200" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {cols.map((c) => (
                <th key={c.key} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-stone-500 font-semibold ${c.numeric ? 'text-right' : 'text-left'}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={idx}
                data-testid={REPORTS.row(idx)}
                onClick={() => onRowClick && onRowClick(r)}
                className={`border-b border-stone-100 hover:bg-stone-50 ${onRowClick ? 'cursor-pointer' : ''}`}
              >
                {cols.map((c) => {
                  const raw = r[c.key];
                  const display = c.format ? c.format(raw) : (raw == null ? '—' : (typeof raw === 'number' ? raw.toLocaleString() : raw));
                  return (
                    <td key={c.key} className={`px-3 py-1.5 ${c.numeric ? 'text-right tabular-nums' : ''} ${c.className || ''}`}>
                      {c.bar ? (
                        <div className="inline-flex items-center gap-2 justify-end w-full">
                          <div className="h-1.5 bg-stone-100 rounded overflow-hidden w-16">
                            <div className="h-full bg-stone-800"
                              style={{ width: `${Math.min(100, (raw / (c.barMax || Math.max(...rows.map(x => x[c.key] || 0), 1))) * 100)}%` }} />
                          </div>
                          <span>{display}</span>
                        </div>
                      ) : display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
